import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Network, Server, Shield, Monitor, Lightbulb, Sun, Database, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SectionHeader } from '../components/common/SectionHeader';
import { CTASection } from '../components/common/CTASection';
import { GeometricBackground } from '../components/decorations/GeometricBackground';
import { services, deliveryMethodology } from '../data/services';

const iconMap = {
  Phone,
  Network,
  Server,
  Shield,
  Monitor,
  Lightbulb,
  Sun,
  Database,
};

const ServicesPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-hero-pattern overflow-hidden">
        <GeometricBackground />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <Badge className="mb-4 bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20">
              Our Services
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              Technology as a Service
            </h1>
            <p className="text-lg text-primary-foreground/80">
              Comprehensive ICT solutions tailored to your business needs. From unified communications to cybersecurity, we deliver end-to-end support to help your organization thrive.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview Grid */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionHeader
            subtitle="What We Offer"
            title="Our Service Portfolio"
            description="Explore our comprehensive range of ICT services designed to meet the diverse needs of businesses across various industries."
            className="mb-16"
          />
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Server;
              const isAccent = service.color === 'accent';
              
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link to={service.route}>
                    <Card className={`h-full hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer ${
                      service.isSpecial 
                        ? 'bg-gradient-to-br from-accent/5 to-accent/10 hover:border-accent/50' 
                        : 'hover:border-primary/30'
                    }`}>
                      <CardContent className="p-6 flex flex-col h-full">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                          isAccent ? 'bg-accent/10' : 'bg-primary/10'
                        }`}>
                          <IconComponent className={`w-6 h-6 ${isAccent ? 'text-accent' : 'text-primary'}`} />
                        </div>
                        {service.isSpecial && (
                          <Badge variant="secondary" className="mb-3 w-fit bg-accent/10 text-accent">
                            Special Offering
                          </Badge>
                        )}
                        <h3 className="font-semibold text-foreground mb-2">{service.name}</h3>
                        <p className="text-sm text-muted-foreground mb-4 flex-grow">{service.shortDescription}</p>
                        <span className={`inline-flex items-center text-sm font-medium mt-auto ${
                          isAccent ? 'text-accent' : 'text-primary'
                        }`}>
                          Learn More
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TaaS Overview Section */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4">Technology as a Service</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                End-to-End ICT Solutions
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                At Voitech Solutions, we embrace &ldquo;Technology as a Service&rdquo; (TaaS) to deliver comprehensive solutions that empower organizations. Our approach ensures businesses receive end-to-end support from strategic planning to implementation and ongoing maintenance.
              </p>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                With over 40 years of combined expertise, our team of seasoned engineers and ICT professionals crafts tailored solutions that address your unique business challenges.
              </p>
              <Button asChild>
                <Link to="/contact">
                  Discuss Your Requirements
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="/img/header/comprehensive.png"
                alt="African team working on technology solutions"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Delivery Methodology */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionHeader
            subtitle="Our Approach"
            title="How We Deliver"
            description="A proven methodology that ensures successful project outcomes and lasting value."
            className="mb-16"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deliveryMethodology.map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center mb-4 font-bold">
                      {index + 1}
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{phase.step}</h3>
                    <p className="text-sm text-muted-foreground">{phase.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Backup Add-on Section */}
      {/* <section className="section-padding bg-card">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link to="/services/data-backup-replication">
                <Card className="border-l-4 border-l-primary hover:shadow-xl hover:border-l-accent transition-all cursor-pointer">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Database className="w-7 h-7 text-primary" />
                      </div>
                      <div>
                        <Badge className="mb-2">Service</Badge>
                        <h3 className="text-2xl font-bold text-foreground">Data Backup & Replication</h3>
                      </div>
                    </div>
                    <p className="text-muted-foreground mb-6">
                      Protect your critical business data with our comprehensive backup and disaster recovery solutions. Ensure business continuity and rapid recovery from any disruption.
                    </p>
                    <ul className="grid sm:grid-cols-2 gap-3 mb-6">
                      {[
                        "Backup strategy and policy design",
                        "Backup implementation (on-prem, cloud, hybrid)",
                        "Backup monitoring and recovery testing",
                        "Disaster recovery planning (RPO/RTO alignment)",
                        "Replication for business continuity"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground italic bg-muted/50 p-3 rounded-lg flex-grow mr-4">
                        We support solutions such as Veeam for enterprise-grade backup and replication.
                      </p>
                      <span className="inline-flex items-center text-sm font-medium text-primary shrink-0">
                        Learn More
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          </div>
        </div>
      </section> */}

      {/* CTA */}
      <CTASection
        title="Need a Customized Solution?"
        description="Our team is ready to design and implement ICT solutions tailored to your specific requirements."
        primaryButtonText="Request Consultation"
        primaryButtonLink="/contact"
      />
    </div>
  );
};

export default ServicesPage;
